<script setup>
  import { ref, defineAsyncComponent, computed, onMounted } from 'vue'
  import moment from 'moment'

  import { getCurrencySymbol } from "@/utils/currency.js"
  import { formatBalanceWithCommas } from "@/utils/numbers.js"

  import { text } from '@/plugins/utils'

  import { useCardsStore } from '@/store/stores/cards'
  const cardsStore = useCardsStore()
  import { useCurrenciesStore } from '@/store/stores/currencies'
  const currenciesStore  = useCurrenciesStore()

  import { useRoute, useRouter } from 'vue-router'
  const router = useRouter()
  const route = useRoute()

  const BaseLoader = defineAsyncComponent (() =>
      import(/* webpackChunkName: "components/base/BaseLoader" */ '@/components/base/BaseLoader.vue')
  );
  const BaseSpinner = defineAsyncComponent (() =>
      import(/* webpackChunkName: "components/base/BaseSpinner" */ '@/components/base/BaseSpinner.vue')
  );
  const AccountSelect = defineAsyncComponent(() =>
      import(/* webpackChunkName: "AccountSelect" */ '@/components/base/selects/AccountSelect.vue')
  );
  const AmountInput = defineAsyncComponent(() =>
      import(/* webpackChunkName: "AmountInput" */ '@/components/base/inputs/AmountInput.vue')
  );
  const BaseButton = defineAsyncComponent(() =>
      import(/* webpackChunkName: "BaseButton" */ '@/components/base/buttons/BaseButton.vue')
  )
  const BaseCardSnippet = defineAsyncComponent (() => import(/* webpackChunkName: "BaseSnippet" */ '@/views/cards/CardSnippet/BaseSnippet.vue'));
  const InProcessBody = defineAsyncComponent (() => import(/* webpackChunkName: "InProcessBody" */ '@/views/cards/CardPage/Replenishment/statusBodies/InProcessBody.vue'));
  const SuccessBody = defineAsyncComponent (() => import(/* webpackChunkName: "SuccessBody" */ '@/views/cards/CardPage/Replenishment/statusBodies/SuccessBody.vue'));
  const FailedBody = defineAsyncComponent (() => import(/* webpackChunkName: "FailedBody" */ '@/views/cards/CardPage/Replenishment/statusBodies/FailedBody.vue'));

  const loading = ref(true)
  const updatingRate = ref(false)

  const cards = computed(() => {
    return cardsStore.getCards?.filter(({ status }) => status === 'exist') || []
  })

  const selectedCardIndex = computed(() => {
    const itemId = computed(() => route.params.id);
    const index = cards.value.findIndex(({ id }) => id === itemId.value)
    return  index >= 0 ? index : 0
  })

  const start =()=> {
    replenishmentStatus.value = 'processing'
    replenishmentInProcess.value = true

    setTimeout(() => {
      replenishmentStatus.value = Math.random() < 0.5 ? 'success' : 'error'
    }, 2500)
  }
  const close =()=> {
    router.back()
  }

  const selectedAccount = ref(null)
  const amount = ref(null)
  const replenishmentInProcess = ref(false)
  const replenishmentStatus = ref(null)

  const amountInAccountCurrency = computed(() => {
    return rate.value * amount.value
  })

  const accountCurrency = computed(() => {
    return selectedAccount.value?.currency
  })
  const cardCurrency = computed(() => {
    return cards.value[selectedCardIndex.value]?.rules.currency
  })

  const insufficientFunds = computed(() => {
    return amountInAccountCurrency.value > selectedAccount.value?.value
  })
  const limitExceeded = computed(() => {
    const { limits } = selectedAccount?.value || {}

    if (limits) {
      return amountInAccountCurrency.value < limits.min || amountInAccountCurrency.value > limits.max
    } else {
      return false
    }
  })

  const actionBtnText = computed(() => {
    if (insufficientFunds.value && !limitExceeded.value) {
      return text('card_page.replenishment.action.not_enough_balance')
    } else if (limitExceeded.value) {
      return  text('card_page.replenishment.action.exceeds_the_limit')
    } else if (amount.value && cardCurrency.value) {
      return `${text('card_page.replenishment.action.replenishment_on')} ${formatBalanceWithCommas(amount.value)}${getCurrencySymbol(cardCurrency.value)}`
    } else {
      return text('card_page.action.replenishment')
    }
  })

  const rate = computed(() => {
    if (cardCurrency.value && accountCurrency.value) {
      return currenciesStore.getRates[cardCurrency.value][accountCurrency.value] || 1
    }
  })

  const statusSummary = computed(() => {
    return {
      amount: amount.value,
      rate: rate.value,
      amountInAccountCurrency: amountInAccountCurrency.value,
      cardCurrency: cardCurrency.value,
      accountCurrency: accountCurrency.value,
      card: cards.value[selectedCardIndex.value],
      date: moment().format(`DD.MM.YYYY [${text('common.in')}] HH:mm`),
      id: '4e48b4cf-1b07-4b44-8f6f-9dd3138c410e',
    }
  })

  const maxAmount = computed(() => {
    const maxLimit = selectedAccount.value?.limits.max
    return Math.floor(maxLimit/rate.value) + 1
  })

  setInterval(() => {
    updatingRate.value = true;

    setTimeout(() => updatingRate.value = false, 2500)
  }, 20000)

  onMounted(() => { // <-- Оборачиваем вызов fetchCards в onMounted
    loading.value = false

    if (!cardsStore.getCards) {
      cardsStore.fetchCards();
    }
  })
</script>

<template>
  <div class="replenishment-card-page">
    <div v-if="!replenishmentInProcess" class="replenishment-card-page__info">
      <div class="replenishment-card-page__title">
        <div class="icon" @click="close"> < </div>
        <div class="text"> {{ text('card_page.replenishment.title') }} </div>
      </div>
      <BaseLoader v-if="loading || !cards.length" style="margin: 20px 0" />
      <template v-else>
        <div class="replenishment-card-page__body">
          <AccountSelect v-model="selectedAccount" />
          <div class="card-wrapper">
            <div class="card">
              <div class="label">
                {{ text('card_page.replenishment.to_card') }}
              </div>
              <BaseCardSnippet :card="cards[selectedCardIndex]" />
            </div>
          </div>
          <div class="amount-wrapper">
            <AmountInput
              v-model="amount"
              :placeholder="getCurrencySymbol(cards[selectedCardIndex].rules.currency)"
              :error="!!amount && (insufficientFunds || limitExceeded)"
              :max="maxAmount"
            >
              <template #suffix-placeholder>
                {{ text('card_page.replenishment.amount_from') }} {{ formatBalanceWithCommas(selectedAccount.limits.min )}}{{ getCurrencySymbol(accountCurrency) }} {{ text('card_page.replenishment.to') }} {{ formatBalanceWithCommas(selectedAccount.limits.max) }}{{ getCurrencySymbol(accountCurrency) }}
              </template>
              <template #suffix>
                {{ formatBalanceWithCommas(amountInAccountCurrency) }} {{ getCurrencySymbol(accountCurrency) }}
              </template>
              <template v-if="!!amount && (insufficientFunds || limitExceeded)" #error>
                <template v-if="limitExceeded">
                  {{ text('card_page.replenishment.amount_from') }} {{ formatBalanceWithCommas(selectedAccount.limits.min )}}{{ getCurrencySymbol(accountCurrency) }} {{ text('card_page.replenishment.to') }} {{ formatBalanceWithCommas(selectedAccount.limits.max) }}{{ getCurrencySymbol(accountCurrency) }}
                </template>
                <template v-else>
                  {{ text('card_page.replenishment.action.not_enough_balance') }} {{ formatBalanceWithCommas(selectedAccount.value) }} {{ getCurrencySymbol(accountCurrency) }}
                </template>
              </template>
            </AmountInput>
          </div>
          <div class="exchange-wrapper">
            <div class="row">
              <div class="label">
                {{ text('card_page.replenishment.conversion_rate') }}:
              </div>
              <div class="value">
                1 {{ getCurrencySymbol(cards[selectedCardIndex].rules.currency) }} = {{ rate }} {{ getCurrencySymbol(accountCurrency) }}
                <BaseSpinner :active="updatingRate"/>
              </div>
            </div>
            <div class="row">
              <div class="label">
                {{ text('card_page.replenishment.fee') }}:
              </div>
              <div class="value">
                0.25 $
              </div>
            </div>
          </div>
        </div>
      </template>
    </div>
    <div class="replenishment-card-page__statuses" v-else>
      <template v-if="replenishmentStatus === 'processing'">
        <InProcessBody :summary="statusSummary" />
      </template>
      <template v-else-if="replenishmentStatus === 'success'" >
        <SuccessBody :summary="statusSummary" />
      </template>
      <template v-else >
        <FailedBody :summary="statusSummary" />
      </template>
    </div>

    <div class="replenishment-card-page__actions">
      <template v-if="!replenishmentInProcess">
        <BaseButton
          :disabled="!amount || insufficientFunds || limitExceeded"
          @click="start"
        >
          <template #label>
            {{ actionBtnText }}
          </template>
        </BaseButton>
      </template>

      <template v-else>
        <BaseButton
          v-if="replenishmentStatus === 'error'"
          @click="replenishmentInProcess = false"
        >
          <template #label>
            {{ text('common.actions.try_again') }}
          </template>
        </BaseButton>
        <BaseButton
          grayscale
          @click="close"
        >
          <template #label>
            {{ text('common.actions.close') }}
          </template>
        </BaseButton>
      </template>
    </div>
  </div>
</template>

<style lang="scss" scoped>
  .replenishment-card-page {
    height: 100%;
    display: flex;
    justify-content: space-between;
    flex-direction: column;
    gap: 12px;

    padding: 24px;

    &__info {
      display: flex;
      justify-content: space-between;
      flex-direction: column;
      gap: 12px;
    }
    &__title {
      display: flex;
      align-items: center;
      justify-content: space-between;

      margin-bottom: 8px;

      .icon {
        width: 8px;
        flex-shrink: 0;
        cursor: pointer;
      }
      .text {
        width: 100%;

        font-size: 18px;
        font-weight: 500;
        text-align: center;
        transform: translateX(-8px);
        pointer-events: none;
      }
    }

    &__body {
      height: 100%;

      display: flex;
      justify-content: space-between;
      flex-direction: column;
      gap: 12px;

      .card-wrapper {
        display: flex;
        align-items: center;
        justify-content: start;

        margin-bottom: 20px;

        .card {
          min-width: 335px;
          .label {
            margin-bottom: 10px;

            font-size: 12px;
            color: var(--text-color-secondary);
          }
        }

      }

      .amount-wrapper {

      }
      .exchange-wrapper {
        display: flex;
        flex-direction: column;
        gap: 12px;

        padding: 16px 0;
        border-top: 1px solid var(--element-bg);

        .row {
          display: flex;
          justify-content: space-between;

          font-size: 16px;
          .label {
            font-size: 14px;
            color: var(--text-color-secondary);
          }
          .value {
            display: flex;
            align-items: center;
            gap: 10px;
            font-size: 18px;
            font-weight: 500;

            svg {
              cursor: pointer;
            }
          }
        }
      }
    }

    &__statuses {
      height: 100%;
    }

    &__actions {
      display: flex;
      gap: 12px;
    }

    @media (max-width: 920px) {
      &__actions {
        flex-direction: column;
      }
      &__body {
        .exchange-wrapper {
          border-top: 1px solid var(--element-bg);
        }
      }
    }
  }
</style>